import { useEffect, useRef } from 'react';
import { useAccessibility } from '../contexts/AccessibilityContext';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

const getLabel = (el) =>
  el.getAttribute('aria-label') || el.textContent?.trim() || el.getAttribute('title') || el.tagName.toLowerCase();

// Tracks how many modals are simultaneously open so page-level keyboard nav
// (useFocusableNavigation, useListNavigation) can pause itself while any
// modal has focus trapped, instead of stealing N/P/O out from under it.
let openModalCount = 0;

export function isAnyModalOpen() {
  return openModalCount > 0;
}

/**
 * Standard modal-dialog keyboard behavior: focuses the dialog on open, traps
 * Tab/Shift+Tab and N/P/O within it, closes on Escape, locks body scroll
 * while open, and restores focus to whatever triggered the dialog when it
 * closes. Required for switch-scanning AAC software, which relies entirely
 * on keyboard focus to know where the "cursor" currently is.
 */
export function useModalDialog(containerRef, { isOpen, onClose }) {
  const previouslyFocusedRef = useRef(null);
  const { announce } = useAccessibility();

  useEffect(() => {
    if (!isOpen) return undefined;

    previouslyFocusedRef.current = document.activeElement;

    const container = containerRef.current;
    if (!container) return undefined;

    openModalCount += 1;
    document.body.setAttribute('data-modal-open', 'true');

    const getFocusable = () => Array.from(container.querySelectorAll(FOCUSABLE_SELECTOR));

    const first = getFocusable()[0] || container;
    first.focus();

    document.body.style.overflow = 'hidden';

    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && onClose) {
        onClose();
        return;
      }

      if (event.key === 'Tab') {
        const items = getFocusable();
        if (items.length === 0) {
          event.preventDefault();
          return;
        }

        const firstEl = items[0];
        const lastEl = items[items.length - 1];

        if (event.shiftKey && document.activeElement === firstEl) {
          event.preventDefault();
          lastEl.focus();
        } else if (!event.shiftKey && document.activeElement === lastEl) {
          event.preventDefault();
          firstEl.focus();
        }
        return;
      }

      const key = event.key.toLowerCase();
      if (key !== 'n' && key !== 'p' && key !== 'o') return;

      const items = getFocusable();
      if (items.length === 0) return;

      if (key === 'o') {
        const current = document.activeElement;
        if (items.includes(current)) {
          event.preventDefault();
          current.click();
        }
        return;
      }

      event.preventDefault();
      const currentIndex = items.indexOf(document.activeElement);
      let nextIndex;
      if (key === 'n') {
        nextIndex = currentIndex === -1 ? 0 : (currentIndex + 1) % items.length;
      } else {
        nextIndex = currentIndex === -1 ? items.length - 1 : (currentIndex - 1 + items.length) % items.length;
      }

      const next = items[nextIndex];
      next.focus();
      announce(getLabel(next));
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
      openModalCount = Math.max(0, openModalCount - 1);
      if (openModalCount === 0) {
        document.body.removeAttribute('data-modal-open');
      }
      if (previouslyFocusedRef.current && previouslyFocusedRef.current.focus) {
        previouslyFocusedRef.current.focus();
      }
    };
  }, [isOpen, containerRef, onClose, announce]);
}
